// CustomerTable.js
import React from 'react';
import MoonLoader from 'react-spinners/MoonLoader';
import ActionsButton from './ActionButtons';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const CustomerTable = ({
  customers,
  pagesVisited,
  customersPerPage,
  handleSort,
  sortConfig,
  isLoading,
  handleVehiclesButtonClick,
  openEditModal,
  deleteCustomer,
}) => {
  const displayCustomers = customers.slice(
    pagesVisited,
    pagesVisited + customersPerPage
  );

  const columnConfig = [
    { key: 'customerNumber', label: 'Customer #', icon: null },
    { key: 'firstName', label: 'First Name', icon: UserIcon },
    { key: 'lastName', label: 'Last Name', icon: UserIcon },
    { key: 'customerType', label: 'Type', icon: BuildingOfficeIcon },
  ];

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return (
        <div className="ml-1 opacity-0 group-hover:opacity-40 transition-opacity">
          <ArrowUpIcon className="h-2.5 w-2.5" />
        </div>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUpIcon className="ml-1 h-3 w-3 text-blue-600" />
    ) : (
      <ArrowDownIcon className="ml-1 h-3 w-3 text-blue-600" />
    );
  };

  const getCustomerTypeBadge = (type) => {
    if (type === 'Corporate') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
          <BuildingOfficeIcon className="h-3 w-3" />
          Corporate
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
        <UserCircleIcon className="h-3 w-3" />
        Private
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 bg-gradient-to-br from-blue-50 to-indigo-50">
        <MoonLoader size={60} color="#3b82f6" />
        <p className="mt-6 text-sm font-medium text-gray-600 animate-pulse">
          Loading customers...
        </p>
      </div>
    );
  }

  if (displayCustomers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 bg-gradient-to-br from-gray-50 to-slate-50">
        <div className="p-4 bg-gray-100 rounded-full mb-4">
          <UserCircleIcon className="h-16 w-16 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No customers found</h3>
        <p className="text-sm text-gray-500 mb-6">
          Try adjusting your search or create a new customer
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr className="bg-gradient-to-r from-gray-50 to-slate-50">
            {columnConfig.map(({ key, label }) => (
              <th
                key={key}
                scope="col"
                onClick={() => handleSort(key)}
                className="group px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors duration-150 select-none"
              >
                <div className="flex items-center gap-1">
                  <span>{label}</span>
                  <SortIcon columnKey={key} />
                </div>
              </th>
            ))}
            <th
              scope="col"
              className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wide bg-gradient-to-r from-gray-50 to-slate-50"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {displayCustomers.map((customer, index) => (
            <tr
              key={customer.id || index}
              className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 group"
            >
              <td className="px-3 py-2 whitespace-nowrap">
                <div className="flex items-center">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-mono font-semibold bg-gray-100 text-gray-800 border border-gray-200 group-hover:border-blue-300 group-hover:bg-blue-50 group-hover:text-blue-800 transition-all">
                    {customer.customerNumber || 'N/A'}
                  </span>
                </div>
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                <div className="flex items-center">
                  <span className="text-sm font-semibold text-gray-800 tracking-wide">
                    {customer.firstName || 'N/A'}
                  </span>
                </div>
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                <span className="text-sm font-semibold text-gray-800 tracking-wide">
                  {customer.lastName || 'N/A'}
                </span>
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                {getCustomerTypeBadge(customer.customerType)}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                <ActionsButton
                  customer={customer}
                  handleVehiclesButtonClick={handleVehiclesButtonClick}
                  openEditModal={openEditModal}
                  deleteCustomer={deleteCustomer}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CustomerTable;
